import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useToastStore } from '@/utils/toast';
import { useCookies } from '@/utils/cookies';

import PageOverview from '@/views/PageOverview.vue';
import { refreshToken } from '@/utils/api';

import mixpanel from '@/mixpanel.ts';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'overview',
      component: PageOverview,
      meta: { auth: true },
    },
    {
      path: '/event-compare',
      name: 'event-compare',
      component: () => import('@/views/PageEventsCompare.vue'),
      meta: { auth: true },
    },
    {
      path: '/benchmarking',
      name: 'benchmarking',
      component: () => import('@/views/PageBenchmarking.vue'),
      meta: { auth: true, requiresFeature: 'premium_benchmarking' },
    },
    {
      path: '/accounts',
      name: 'accounts',
      component: () => import('@/views/PageAccounts.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/api',
      name: 'api',
      component: () => import('@/views/PageAPI.vue'),
      meta: { auth: true },
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/PageContact.vue'),
      meta: { auth: true },
    },
    {
      path: '/events',
      meta: { auth: true },
      children: [
        {
          path: '',
          name: 'events',
          component: () => import('@/views/PageEvents.vue'),
        },
        {
          path: 'purchasing',
          name: 'event-purchasing',
          component: () => import('@/views/PageEventPurchasing.vue'),
        },
      ],
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import('@/views/PageFAQ.vue'),
      meta: { auth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/PageLogin.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/PageLogin.vue'),
      meta: { logout: true },
    },
    {
      path: '/reset/:code/:email',
      name: 'reset',
      component: () => import('@/views/PageLogin.vue'),
      meta: { reset: true },
    },
    {
      path: '/roles',
      name: 'roles',
      component: () => import('@/views/PageRoles.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/sensors',
      name: 'sensors',
      meta: { auth: true },
      redirect: '/sensors/all',
      children: [
        {
          path: 'all',
          name: 'all-sensors',
          component: () => import('@/views/PageSensors.vue'),
        },
        {
          path: 'assigned',
          name: 'assigned-sensors',
          component: () => import('@/views/PageSensorsAssigned.vue'),
          meta: { auth: true },
        },
        {
          path: 'live',
          name: 'live-monitor',
          component: () => import('@/views/PageSensorsMonitor.vue'),
        },
      ],
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/PageUsers.vue'),
      meta: { auth: true },
    },
    {
      path: '/user-profile',
      name: 'user-profile',
      component: () => import('@/views/PageUserProfile.vue'),
      meta: { auth: true },
    },

    {
      path: '/admin/profile/:accountId',
      name: 'admin',
      component: () => import('@/views/PageClientDetails.vue'),
      meta: { auth: true },
    },

    {
      path: '/admin/documents',
      meta: { auth: true },
      children: [
        {
          path: '',
          name: 'documents',
          component: () => import('@/views/PageDocuments.vue'),
        },
        {
          path: ':id',
          name: 'document',
          component: () => import('@/views/PageDocumentDetail.vue'),
        },
      ],
    },

    {
      path: '/feature-flags',
      name: 'feature-flags',
      component: () => import('@/views/PageFeatureFlags.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/PageSettings.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/PageNotFound.vue'),
      meta: { auth: true },
    },
    {
      path: '/unauthorised',
      name: 'unauthorised',
      component: () => import('@/views/PageUnauthorised.vue'),
      meta: { auth: true },
    },
  ],
});

router.beforeResolve(async (to) => {
  const userStore = useUserStore();
  const toast = useToastStore();
  const cookies = useCookies();

  const expiry = cookies?.get('expiry');

  if (!userStore.isLoggedIn) {
    // Restore from cookies if we can
    userStore.restoreFromCookies();
  }

  if (to.path === '/login') {
    return true;
  }

  // If we don't care about login status, skip.
  if (to.meta && to.meta.auth) {
    // If we aren't logged in...
    if (!userStore.isLoggedIn) {
      if (!expiry && !cookies.get('user')) {
        return '/login';
      }

      // Restore from cookies if we can
      userStore.restoreFromCookies();
    }

    if (
      (to.meta.requiresMeshh && userStore.isNonStaff) ||
      (to.meta.requiresFeature && !userStore.hasFeature(to.meta.requiresFeature as string))
    ) {
      if (to.meta.unauthorizedMessage) {
        return {
          path: '/unauthorised',
          query: {
            header: (to.meta.unauthorizedMessage as { header: string }).header,
            description: (to.meta.unauthorizedMessage as { description: string }).description,
          },
        };
      }

      return '/unauthorised';
    }

    const expiredMessage = {
      response: {
        data: {
          message: 'Your session has expired. Please log in again.',
        },
      },
    };

    // Check if token is expired or expires within 6 hours
    const now = new Date();
    const expiryDate = cookies?.get('expiry') ? new Date(cookies.get('expiry')) : null;
    const sixHoursFromNow = new Date(now.getTime() + 6 * 60 * 60 * 1000); // 6 hours in milliseconds

    if (!expiryDate || expiryDate <= sixHoursFromNow) {
      if (cookies?.get('token')) {
        return refreshToken(cookies.get('token')).then(
          (response) => {
            cookies.set('token', response.accessToken);
            cookies.set('expiry', response.expires_at);
            userStore.updateToken(response.accessToken);
            return true;
          },
          () => {
            toast.error(expiredMessage);
            return '/login';
          },
        );
      } else {
        toast.error(expiredMessage);
        return '/login';
      }
    }
  }

  return true;
});

type PageTitle = {
  [key: string]: string;
};

const pageTitles: PageTitle = {
  overview: 'Overview',
  api: 'API',
  accounts: 'Accounts',
  contact: 'Contact Us',
  events: 'Events',
  faq: 'FAQ',
  login: 'Login',
  logout: 'Logout',
  reset: 'Password Reset',
  roles: 'Roles',
  sensors: 'Sensors',
  'all-sensors': 'All Sensors',
  'assigned-sensors': 'Assigned Sensors',
  'live-monitor': 'Live Monitor',
  users: 'Users',
  'user-profile': 'User Profile',
  admin: 'Admin',
  'event-compare': 'Event Compare',
  'benchmarking': 'Benchmarking',
  unauthorised: 'Unauthorised',
  'not-found': 'Not Found',
};

router.afterEach((to) => {
  const name = to.name as keyof PageTitle;
  const title = pageTitles[name] || 'Unknown Page';
  mixpanel.track(`View ${title}`, {
    title,
    path: to.path,
  });
});

export default router;
